import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import PosLayout from "../layouts/PosLayout";
import Addons from "../pages/configurations/Addons";
import AddonsList from "../pages/configurations/AddonsList";
import AttributeValue from "../pages/configurations/AttributeValue";
import AttributesList from "../pages/configurations/AttributesList";
import CategoriesList from "../pages/configurations/CategoriesList";
import Category from "../pages/configurations/Category";
import Configurations from "../pages/configurations/Configurations";
import DineAreasList from "../pages/configurations/DineAreasList";
import ItemsList from "../pages/configurations/ItemsList";
import CustomerView from "../pages/customer-view/CustomerView";
import Customers from "../pages/customers/Customers";
import Dashboard from "../pages/dashboard/Dashboard";
import OrderHistory from "../pages/history/History";
import KOTView from "../pages/kot-view/KOTView";
import Login from "../pages/login/Login";
import Menu from "../pages/menu/Menu";
import Orders from "../pages/orders/Orders";
import Reports from "../pages/reports/Reports";
import Settings from "../pages/settings/Settings";
import Tables from "../pages/tables/Tables";
import UserProfile from "../pages/user-profile/UserProfile";
import UserView from "../pages/user-view/UserView";
import Users from "../pages/users/Users";
import { loadUserSession } from "../reduxStore/slices/authSlice";
import ProtectedRoutes from "./ProtectedRoutes";
import Item from "../pages/configurations/Item";
import LandingPage from "../pages/site/LandingPage";
import BookDemo from "../pages/site/BookDemo";
import ExploreAllFeatures from "../pages/site/ExploreAllFeatures";
import Pricing from "../pages/site/Pricing";
import OrderDetailsPage from "../pages/orders/OrderDetailsPage";
import TermsOfServices  from "../pages/site/TermsOfServices";
import PrivacyPolicy from "../pages/site/PrivacyPolicy";
import CancellationRefund from "../pages/site/CancellationRefund";
import ContactUs from "../pages/site/ContactUs";
import Sidebar from "../pages/site/Sidebar";
import Faqs from "../pages/site/Faqs";
import AboutUs from "../pages/site/AboutUs";
import FoodCourt from "../pages/outlet-types/FoodCourt";
import CafeShops from "../pages/outlet-types/CafeShops";
import CloudKitchen from "../pages/outlet-types/CloudKitchen";
import IceCream from "../pages/outlet-types/IceCream";
import Bakery from "../pages/outlet-types/Bakery";
import FineDine from "../pages/outlet-types/FineDine";
import QuickService from "../pages/outlet-types/QuickService";
import Pizzeria from "../pages/outlet-types/Pizzeria";
import BarBrewery from "../pages/outlet-types/BarBrewery";
import LargeChain from "../pages/outlet-types/LargeChain";

function Routers() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthLoaded } = useSelector((state) => state.auth);

  // Only load user session for /app routes
  useEffect(() => {
    if (location.pathname.startsWith("/app")) {
      dispatch(loadUserSession());
      document.body.classList.add("app-body-style");
    } else {
      document.body.classList.remove("app-body-style");
    }
  }, [dispatch, location.pathname]);

  // Do NOT wait for isAuthLoaded on public routes

  const isAppRoute = location.pathname.startsWith("/app");

  // Wait for auth ONLY on /app routes
  if (isAppRoute && !isAuthLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <Routes>
      <Route element={<ProtectedRoutes allowedRoles={["owner","store_counter_owner","store_counter_user"]} />}>
        <Route path="/app" element={<PosLayout />}>
          <Route index element={<Navigate to="/app/home" replace />} />
          <Route path="home" element={<Dashboard />} />
          <Route path="tables" element={<Tables />} />
          <Route path="menu" element={<Menu />} />
          <Route path="kot-view" element={<KOTView />} />
          <Route path="orders" element={<Orders />} />
          <Route path="order/:id" element={<OrderDetailsPage />} />
          <Route path="order-history" element={<OrderHistory />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/view/:uid" element={<CustomerView />} />
          <Route path="reports" element={<Reports />} />
          <Route path="users" element={<Users />} />
          <Route path="users/view/:uid" element={<UserView />} />
          <Route path="user-profile" element={<UserProfile />} />
          <Route path="config" element={<Configurations />} />
          <Route path="config/categories" element={<CategoriesList />} />
          <Route path="config/category/add" element={<Category />} />
          <Route path="config/category/edit/:uid" element={<Category />} />
          <Route path="config/items" element={<ItemsList />} />
          <Route path="config/item/add" element={<Item />} />
          <Route path="config/item/edit/:uid" element={<Item />} />
          <Route path="config/attributes-values" element={<AttributesList />} />
          <Route
            path="config/attribute-value/add"
            element={<AttributeValue />}
          />
          <Route
            path="config/attribute-value/edit/:uid"
            element={<AttributeValue />}
          />
          <Route path="config/addons" element={<AddonsList />} />
          <Route path="config/addon/add" element={<Addons />} />
          <Route path="config/addon/edit/:uid" element={<Addons />} />
          <Route path="config/dine-tables" element={<DineAreasList />} />
          <Route path="settings" element={<Settings />} />          
        </Route>
      </Route>

      <Route path="/" element={<LandingPage />} />
      <Route path="/pos/book-demo" element={<BookDemo />} />
      <Route
        path="/pos/explore-all-features"
        element={<ExploreAllFeatures />}
      />
      <Route path="/pos/pricing" element={<Pricing />} />
      <Route path="/pos/terms-and-conditions" element={<TermsOfServices />} />
      <Route path="/pos/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/pos/cancellation-refund-policy" element={<CancellationRefund />} />
      <Route path="/pos/contact-us" element={<ContactUs />} />
      <Route path="/pos/faqs" element={<Faqs />} />
      <Route path="/pos/about-us" element={<AboutUs />} />
      <Route path="/pos/sidebar" element={<Sidebar />} />
      <Route path="/pos/food-court" element={<FoodCourt />} />
      <Route path="/pos/cafe-shops" element={<CafeShops />} />
      <Route path="/pos/cloud-kitchen" element={<CloudKitchen />} />
      <Route path="/pos/ice-cream" element={<IceCream />} />
      <Route path="/pos/bakery" element={<Bakery />} />
      <Route path="/pos/fine-dine" element={<FineDine />} />
      <Route path="/pos/qsr" element={<QuickService />} />
      <Route path="/pos/pizzeria" element={<Pizzeria />} />
      <Route path="/pos/bar-brewery" element={<BarBrewery />} />
      <Route path="/pos/large-chain" element={<LargeChain />} />
      {/* <Route path="/outlets/:type" element={<OutletDetail />} /> */}
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<div>Not found</div>} />
    </Routes>
  );
}

export default Routers;

// //<Link to="/books/1">Link 1</Link>
// //<Link to="{`/books/${number}`}">Link 1</Link>
